/* =========================================================================
   hh-quiz.js — the unified Hildegard diagnosis + product-matching quiz.
   Vanilla-JS port of Quiz.jsx + QuizQuestion.jsx (the theme has no React).

   Reads the pure-JS engine globals (loaded before this file):
     • HH_QUIZ_QUESTIONS      (product questions: top_concern, skin_type, hair_goal)
     • HH_DIAGNOSIS_QUESTIONS (intro + Hildegard markers)
     • HH_QUIZ_FLOW           (the master step order)
     • isConstitutionTie      (gates the conditional `veins` step)

   Flow per Quiz.jsx:
     - displaySteps from HH_QUIZ_FLOW (subPartOf questions render inline as a
       secondary — none currently use it, but supported).
     - activeSteps filters conditionals: showIf (frame after a lean pinch) and
       showIfTie (veins on an exact constitution tie).
     - progress = (idx+1) / (activeSteps+1); the +1 reserves the account/reveal slot.
     - on finish: persist answers to localStorage and redirect to the reveal page.

   The DOM is built with a tiny hyperscript helper (createElement + textContent,
   NO innerHTML) — all text goes through textContent, so config copy and answers
   can never inject markup. Mounts on every [data-hh-quiz].
   ========================================================================= */
(function () {
  'use strict';

  var STORE_KEY = 'hh_quiz_answers';        // same object feeds both engines (Quiz.jsx)
  var DIAG_KEY = 'hh_diagnosis_answers';

  // h(tag, props, children[]) — props: class / text / on<Event> / attrs. Text is
  // set via textContent and children appended as nodes, so nothing is parsed as HTML.
  function h(tag, props, children) {
    var el = document.createElement(tag);
    if (props) {
      Object.keys(props).forEach(function (k) {
        var v = props[k];
        if (v == null) return;
        if (k === 'class') el.className = v;
        else if (k === 'text') el.textContent = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
        else el.setAttribute(k, v);
      });
    }
    (children || []).forEach(function (c) {
      if (c == null || c === false) return;
      el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return el;
  }

  function allQuestions() {
    return [].concat(window.HH_QUIZ_QUESTIONS || [], window.HH_DIAGNOSIS_QUESTIONS || []);
  }

  // ---- birthdate bounds --------------------------------------------------
  // A date input's value is a "YYYY-MM-DD" string, so ISO strings compare
  // lexicographically — plain >= / <= is a correct range test.
  //
  // The floor is load-bearing, not decoration: Chromium (Chrome AND Edge) fires
  // `change`/`input` on EVERY keystroke in the year segment, so typing "1985"
  // walks the value through 0001 → 0019 → 0198 → 1985. Each of those is a
  // *complete, valid* date as far as the browser is concerned. Without a floor
  // the quiz would happily accept year 0001 from someone who typed one digit
  // and clicked Next. `min`/`max` on the element only constrain the picker and
  // set :invalid — they do NOT clear an out-of-range value — so the guard here
  // is what actually enforces it.
  var DATE_MIN = '1900-01-01';
  function todayISO() {
    var d = new Date();
    var mo = String(d.getMonth() + 1), da = String(d.getDate());
    return d.getFullYear() + '-' + (mo.length < 2 ? '0' + mo : mo) + '-' + (da.length < 2 ? '0' + da : da);
  }
  function isPlausibleDate(v) {
    if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    return v >= DATE_MIN && v <= todayISO();
  }

  function buildDisplaySteps() {
    var all = allQuestions();
    var byId = {}, subMap = {};
    all.forEach(function (q) {
      byId[q.id] = q;
      if (q.subPartOf) subMap[q.subPartOf] = q;
    });
    var order = window.HH_QUIZ_FLOW ||
      all.filter(function (q) { return !q.subPartOf; }).map(function (q) { return q.id; });
    var steps = [];
    order.forEach(function (id) {
      var q = byId[id];
      if (q && !q.subPartOf) steps.push({ primary: q, secondary: subMap[q.id] || null });
    });
    return steps;
  }

  function computeActiveSteps(displaySteps, answers) {
    return displaySteps.filter(function (s) {
      var p = s.primary;
      if (p.showIf) return answers[p.showIf.question] === p.showIf.equals;
      if (p.showIfTie) return !!(window.isConstitutionTie && window.isConstitutionTie(answers));
      return true;
    });
  }

  function isQAnswered(q, answers) {
    if (q.optional || q.intro) return true;
    var a = answers[q.id];
    if (q.type === 'multi') return Array.isArray(a) && a.length > 0;
    if ((q.layout || 'chip') === 'date') return isPlausibleDate(a);
    return a != null && a !== '';
  }

  // Q2-style derived answer sets (derivedFrom) — supported though unused today.
  function resolveAnswers(q, displaySteps, answers) {
    if (q.derivedFrom) {
      var sourceQ = displaySteps.map(function (s) { return s.primary; })
        .find(function (x) { return x.id === q.derivedFrom; });
      var pickedId = answers[q.derivedFrom];
      var src = (sourceQ && sourceQ.answers) ? sourceQ.answers : [];
      return src.filter(function (a) { return a.id !== pickedId && !a.isNoneOption; })
        .concat(q.noneAnswer ? [q.noneAnswer] : []);
    }
    return q.answers || [];
  }

  // ---- per-variant answer cells -----------------------------------------
  function chipCells(q, opts, answer, onPick) {
    var isMulti = q.type === 'multi';
    var set = isMulti ? (Array.isArray(answer) ? answer : []) : (answer ? [answer] : []);
    return opts.map(function (c) {
      var on = set.indexOf(c.id) !== -1;
      var kids = [h('span', { text: c.label })];
      if (c.examples) kids.push(h('span', { class: 'hh-quiz__chip-eg', text: '— ' + c.examples }));
      return h('button', {
        type: 'button', class: 'hh-quiz__chip' + (on ? ' is-on' : ''),
        'aria-pressed': String(on), onClick: function () { onPick(c.id); }
      }, kids);
    });
  }

  function optionCells(q, opts, answer, cls, role, onPick) {
    return opts.map(function (c) {
      var on = answer === c.id;
      return h('button', {
        type: 'button', class: cls + (on ? ' is-on' : ''), role: role, 'aria-checked': String(on),
        onClick: function () { onPick(c.id); }
      }, [c.label]);
    });
  }

  // One question block: prompt + optional subhead/image/footnote + the answer body.
  function questionNode(q, displaySteps, answers, eyeImg, onPick) {
    var kids = [];
    kids.push(h('h2', { class: 'hh-quiz__prompt' + (q.subhead ? ' hh-quiz__prompt--withsub' : ''), text: q.promptText }));
    if (q.subhead) kids.push(h('p', { class: 'hh-quiz__subhead', text: q.subhead }));
    if (q.image) {
      var src = q.image.indexOf('eye-central') !== -1 && eyeImg ? eyeImg : q.image;
      var img = h('img', { src: src, alt: q.imageAlt || '' });
      img.addEventListener('error', function () { img.style.display = 'none'; });
      kids.push(h('div', { class: 'hh-quiz__img' }, [img]));
    }

    var opts = resolveAnswers(q, displaySteps, answers);
    var layout = q.layout || 'chip';
    var val = answers[q.id];
    if (layout === 'card') {
      kids.push(h('div', { class: 'hh-quiz__cards', role: 'radiogroup', 'aria-label': q.promptText }, optionCells(q, opts, val, 'hh-quiz__card', 'radio', onPick)));
    } else if (layout === 'yesno') {
      kids.push(h('div', { class: 'hh-quiz__yns', role: 'radiogroup', 'aria-label': q.promptText }, optionCells(q, opts, val, 'hh-quiz__yn', 'radio', onPick)));
    } else if (layout === 'date') {
      var input = h('input', {
        type: 'date', class: 'hh-quiz__date-input', value: val || '',
        min: DATE_MIN, max: todayISO(), 'aria-label': q.promptText
      });
      // A date input holds LIVE editing state inside the browser's own segmented
      // widget, and Chromium fires input/change on every year keystroke. So this
      // one control must NOT go through the re-rendering setter: rebuilding the
      // node after the first year digit destroys the widget mid-edit, drops
      // focus, and the rest of the year is typed into nothing (the visitor ends
      // up stuck on year 0001). `onPick` here is the QUIET setter — it updates
      // state and the Next button only, leaving this node alive. Both events are
      // bound because engines disagree on which one lands per keystroke.
      input.addEventListener('input', function () { onPick(input.value); });
      input.addEventListener('change', function () { onPick(input.value); });
      kids.push(h('div', { class: 'hh-quiz__date' }, [input]));
    } else {
      kids.push(h('div', { class: 'hh-quiz__chips', role: q.type === 'multi' ? 'group' : 'radiogroup', 'aria-label': q.promptText },
        chipCells(q, opts, val, onPick)));
    }
    if (q.footnote) kids.push(h('p', { class: 'hh-quiz__footnote', text: q.footnote }));
    return h('div', { class: 'hh-quiz__q' }, kids);
  }

  function introNode(q) {
    var kids = [h('h2', { class: 'hh-quiz__intro-title', text: q.promptText })];
    String(q.body || '').split('\n\n').forEach(function (p) {
      kids.push(h('p', { class: 'hh-quiz__intro-p', text: p }));
    });
    return h('div', { class: 'hh-quiz__intro' }, kids);
  }

  // ---- controller --------------------------------------------------------
  function init(mount) {
    if (mount.__hhQuizInit) return;
    mount.__hhQuizInit = true;

    if (!window.HH_QUIZ_FLOW && !window.HH_DIAGNOSIS_QUESTIONS) {
      mount.appendChild(h('p', { class: 'hh-quiz__error', text: 'Quiz is loading…' }));
      return;
    }

    var redirectUrl = mount.getAttribute('data-redirect') || '/';
    var eyeImg = mount.getAttribute('data-eye-image') || '';
    var displaySteps = buildDisplaySteps();
    var state = { step: 0, answers: {} };

    function activeSteps() { return computeActiveSteps(displaySteps, state.answers); }

    // The progress rail is the persistent node in the sticky header chrome
    // (layout/theme.liquid). Cache it once; update width + aria each render.
    var progressEl = document.querySelector('[data-hh-quiz-progress]');
    var progressFill = document.querySelector('[data-hh-quiz-progress-fill]');
    function updateProgress(idx, total, pct) {
      if (progressFill) progressFill.style.width = pct + '%';
      if (progressEl) {
        progressEl.hidden = false;
        progressEl.setAttribute('aria-valuenow', String(idx + 1));
        progressEl.setAttribute('aria-valuemax', String(total));
      }
    }

    // refreshNav is re-pointed by every render at that render's own Next button,
    // so the quiet setter can re-gate it without rebuilding the step.
    var refreshNav = function () {};

    function commitAnswer(qid, val) {
      var next = Object.assign({}, state.answers);
      next[qid] = val;
      allQuestions().forEach(function (q) {           // prune stale conditional answers (Quiz.jsx)
        if (q.showIf && next[q.showIf.question] !== q.showIf.equals) delete next[q.id];
        else if (q.showIfTie && !(window.isConstitutionTie && window.isConstitutionTie(next))) delete next[q.id];
      });
      state.answers = next;
    }

    // Chips / cards / yes-no are stateless buttons — rebuilding them is how their
    // selected styling updates, so they take the rendering path.
    function setAnswer(qid, val) { commitAnswer(qid, val); render(); }

    // Native inputs render their own value and hold live editing state, so they
    // take the quiet path: state + Next button, no DOM teardown. See the date
    // branch in questionNode for why this is mandatory and not an optimisation.
    function setAnswerQuiet(qid, val) { commitAnswer(qid, val); refreshNav(); }

    // Multi-select chip toggle with exclusive ("none") handling (ChipQuestion).
    function toggleChip(q, aid) {
      if (q.type !== 'multi') { setAnswer(q.id, aid); return; }
      var chip = (q.answers || []).find(function (a) { return a.id === aid; });
      var current = Array.isArray(state.answers[q.id]) ? state.answers[q.id].slice() : [];
      if (chip && chip.isExclusive) { setAnswer(q.id, current.indexOf(aid) !== -1 ? [] : [aid]); return; }
      var exclusiveIds = (q.answers || []).filter(function (a) { return a.isExclusive; }).map(function (a) { return a.id; });
      var nextArr = current.filter(function (id) { return exclusiveIds.indexOf(id) === -1; });
      if (nextArr.indexOf(aid) !== -1) nextArr = nextArr.filter(function (id) { return id !== aid; });
      else nextArr.push(aid);
      setAnswer(q.id, nextArr);
    }

    function pickFor(q) {
      var layout = q.layout || 'chip';
      if (layout === 'chip') return function (aid) { toggleChip(q, aid); };
      if (layout === 'date') return function (v) { setAnswerQuiet(q.id, v); };
      return function (aid) { setAnswer(q.id, aid); };
    }

    function finish() {
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(state.answers));
        localStorage.setItem(DIAG_KEY, JSON.stringify(state.answers));
        // Re-arm the opt-in gate so a retake re-POSTs the updated diagnosis
        // (SP6 Phase D — hh-account-gate.js skips when this flag is '1').
        localStorage.removeItem('hh_gate_passed');
      } catch (e) { /* private mode — non-fatal */ }
      window.location.href = redirectUrl;
    }

    function render() {
      var steps = activeSteps();
      var idx = Math.max(0, Math.min(state.step, steps.length - 1));
      state.step = idx;
      var cur = steps[idx];
      var primaryQ = cur.primary, secondaryQ = cur.secondary;
      var showSecondary = !!secondaryQ && (!secondaryQ.showIf || state.answers[secondaryQ.showIf.question] === secondaryQ.showIf.equals);
      var answered = isQAnswered(primaryQ, state.answers) && (!showSecondary || isQAnswered(secondaryQ, state.answers));
      var total = steps.length + 1;                    // +1 reserves the reveal slot
      var pct = ((idx + 1) / total) * 100;
      var isLast = idx === steps.length - 1;

      var body;
      if (primaryQ.intro) {
        body = introNode(primaryQ);
      } else {
        var nodes = [questionNode(primaryQ, displaySteps, state.answers, eyeImg, pickFor(primaryQ))];
        if (showSecondary) nodes.push(h('div', { class: 'hh-quiz__secondary' }, [questionNode(secondaryQ, displaySteps, state.answers, eyeImg, pickFor(secondaryQ))]));
        body = h('div', {}, nodes);
      }

      var nextLabel = primaryQ.intro ? (primaryQ.cta || 'Begin') : (isLast ? 'Reveal My Diagnosis' : 'Next');

      // Progress lives in the sticky header chrome (layout/theme.liquid) as a
      // PERSISTENT node — we only update its width here, so the CSS width
      // transition glides between steps instead of snapping (a fresh element
      // each render would have no prior value to animate from).
      updateProgress(idx, total, pct);

      var prevBtn = h('button', { type: 'button', class: 'hh-quiz__prev', text: 'Previous', onClick: function () { if (idx > 0) { state.step = idx - 1; render(); } } });
      if (idx === 0) prevBtn.disabled = true;
      var nextBtn = h('button', { type: 'button', class: 'hh-quiz__next', text: nextLabel, onClick: function () { if (idx < steps.length - 1) { state.step = idx + 1; render(); } else finish(); } });
      if (!answered) nextBtn.disabled = true;

      // Let the quiet setter re-gate THIS render's Next button in place.
      refreshNav = function () {
        nextBtn.disabled = !(isQAnswered(primaryQ, state.answers) &&
          (!showSecondary || isQAnswered(secondaryQ, state.answers)));
      };

      var root = h('div', { class: 'hh-quiz', 'data-step': idx }, [
        h('div', { class: 'hh-quiz__head' }, [
          // Per-step eyebrow override (copy lives in diagnosis-config.js, not here).
          // The intro uses it to say the diagnostic is for women before anything else
          // on the page — a man took the quiz and was confused to get a woman's reading.
          h('div', { class: 'hh-quiz__eyebrow', text: primaryQ.eyebrow || 'Hildegard Diagnostic' })
        ]),
        h('div', { class: 'hh-quiz__body' }, [body]),
        h('div', { class: 'hh-quiz__nav' }, [prevBtn, nextBtn])
      ]);

      mount.replaceChildren(root);
      window.scrollTo({ top: 0, behavior: 'auto' });
    }

    render();
  }

  function boot() {
    var mounts = document.querySelectorAll('[data-hh-quiz]');
    for (var i = 0; i < mounts.length; i++) init(mounts[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
